<!-- 
  @file GetMyQuote.vue
  @description
  Handles the "Get My Quote" step of the finance application process. This view
  displays vehicle information and allows users to proceed to the driving licence step.

  @components
  - VehicleDetails: Displays selected vehicle information
  - Button: Navigation control with theme-aware styling
  - PrivacyPolicy: Displays privacy policy information
  - FinanceDisclaimer: Shows finance-related disclaimers

  @stores
  - rootStore: Manages application-wide state and progress tracking
  - vehicleStore: Handles vehicle data management
  - configStore: Controls theme configuration

  @composables
  - useStoreOperation: Handles async operations with error management
  - usePageNavigation: Handles navigation logic

  @features
  - Responsive layout with max-width constraint
  - Theme-aware styling (supports default and custom themes)
  - Loading state management during navigation
  - Progress tracking integration
  - Full accessibility support
  - Error handling with user feedback

  @state
  - isLoading: boolean - Controls loading state during navigation
  - vehicle: Vehicle - Current vehicle details from vehicleStore
  - isDefaultTheme: boolean - Theme configuration state
  - header: string - Dynamic header text based on vehicle presence
-->
<template>
    <div class="get-my-quote max-w-screen-sm mx-auto my-2" @keyup.enter="handleContinue">
        <h1 class="text-h2 mb-2" :class="[vehicle?.vrm ? '' : 'mt-3']">{{ header }}</h1>

        <VehicleDetails
            class="mt-4"
            v-if="vehicle.vrm" 
            :show-vehicle-image="!!vehicle.imageUrl" 
            :vehicle="{
                ...vehicle,
                make: vehicle.make || '',
                model: vehicle.model || '',
                price: vehicle.price || 0,
                colour: vehicle.colour === 'undefined' ? '' : vehicle.colour || '',
                transmission: vehicle.transmission === 'undefined' ? '' : vehicle.transmission || '',
                fuelType: vehicle.fuelType === 'undefined' ? '' : vehicle.fuelType || '',
                mileage: vehicle.mileage === 'undefined' ? '' : vehicle.mileage || '',
                imageUrl: vehicle.imageUrl || '',
                dealerInfoUrl: vehicle.dealerInfoUrl || '',
                trim: vehicle.trim || '',
                registrationYear: vehicle.registrationYear?.toString() || ''
            }"
        />
        
        <Button 
            :id="createElementId(Page.GET_MY_QUOTE, ElementType.CONTINUE)"
            :textColor="buttonTextColorClass"
            textSize="text-button-large"
            :hover-color="`${buttonColorClass} hover:brightness-110`"
            :color="buttonColorClass"
            :label="NAVIGATION_CONTENT.CONTINUE"
            width="100%"
            height="56px"
            @click="handleContinue"
            :disabled="isLoading"
            :loading="isLoading"
            class="mt-3"
            :iconAfter="true"
            :iconColor="iconColorClass"
        />

        <PrivacyPolicy :showBenefits="false" />

        <FinanceDisclaimer />
    </div>
</template>
  
<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { createElementId, Page, ElementType } from '@/utils/elementIds';
import { useRootStore } from '@/stores/rootStore';
import { useStoreOperation } from '@/composables/useStoreOperation';
import { usePageNavigation } from '@/composables/usePageNavigation';
import PrivacyPolicy from '@/components/PrivacyPolicy.vue';
import FinanceDisclaimer from '@/components/FinanceDisclaimer.vue';
import VehicleDetails from '@/components/VehicleDetails.vue';
import { useVehicleStore } from '@/stores/modules/vehicleStore';
import { useConfigStore } from '@/stores/modules/configStore';

const store = useRootStore();
const vehicleStore = useVehicleStore();
const vehicle = computed(() => vehicleStore.vehicle);

const header = computed(() => 
    vehicle.value.vrm 
        ? GET_MY_QUOTE_CONTENT.HEADERS.TITLE 
        : GET_MY_QUOTE_CONTENT.HEADERS.NO_VEHICLE
);

const { isLoading } = useStoreOperation();
const configStore = useConfigStore();

const { GET_MY_QUOTE_CONTENT, NAVIGATION_CONTENT } = configStore.affiliateConfig.contents;

const { isNavigating, handleNavigation } = usePageNavigation('get-my-quote', GET_MY_QUOTE_CONTENT.ERROR_MESSAGE);

const handleContinue = async () => {
    if (isLoading.value || isNavigating.value) return;
    await handleNavigation();
};

const buttonColorClass = computed(() => {
    const div = document.createElement('div');
    div.className = 'bg-secondary';
    document.body.appendChild(div);
    const computedColor = getComputedStyle(div).backgroundColor;
    document.body.removeChild(div);
    
    return computedColor === 'rgb(255, 255, 255)' ? 'bg-primary' : 'bg-secondary';
});

const buttonTextColorClass = computed(() => {
    return buttonColorClass.value === 'bg-primary' ? 'text-white' : 'text-dark-1';
});

const iconColorClass = computed(() => {
    return buttonColorClass.value === 'bg-primary' ? 'hsl(var(--twc-white))' : 'hsl(var(--twc-dark-1))';
});

onMounted(() => {
    store.progress.setPageName('get-my-quote');
});
</script>
  